/**
 * Universal StatusBadge Component
 *
 * A unified status badge component that can be used across all modules.
 * Uses the centralized status configuration from designSystem.ts
 *
 * @example
 * <StatusBadge type="invoice" status="PAID" />
 * <StatusBadge type="rental" status="ACTIVE" size="lg" />
 * <StatusBadge type="payment" status="CONFIRMED" showDot />
 */

import React from 'react';
import { statusColors } from '../../config/designSystem';
import { cn } from '../../utils/cn';

// ============================================================================
// TYPES
// ============================================================================

type StatusType = keyof typeof statusColors;

type InvoiceStatus = keyof typeof statusColors.invoice;
type PaymentStatus = keyof typeof statusColors.payment;
type QuotationStatus = keyof typeof statusColors.quotation;
type RentalStatus = keyof typeof statusColors.rental;
type MaintenanceStatus = keyof typeof statusColors.maintenance;
type ContractStatus = keyof typeof statusColors.contract;
type DeviceStatus = keyof typeof statusColors.device;
type GenericStatus = keyof typeof statusColors.generic;

type StatusValue<T extends StatusType> = T extends 'invoice'
  ? InvoiceStatus
  : T extends 'payment'
  ? PaymentStatus
  : T extends 'quotation'
  ? QuotationStatus
  : T extends 'rental'
  ? RentalStatus
  : T extends 'maintenance'
  ? MaintenanceStatus
  : T extends 'contract'
  ? ContractStatus
  : T extends 'device'
  ? DeviceStatus
  : T extends 'generic'
  ? GenericStatus
  : string;

interface StatusBadgeProps<T extends StatusType> {
  /** The type of status (invoice, payment, rental, etc.) */
  type: T;
  /** The status value */
  status: StatusValue<T>;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Show status indicator dot */
  showDot?: boolean;
  /** Show border */
  bordered?: boolean;
  /** Pill shape (full rounded) */
  pill?: boolean;
  /** Custom label override */
  label?: string;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// SIZE VARIANTS
// ============================================================================

const sizeClasses = {
  xs: 'px-1.5 py-0.5 text-2xs',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

const dotSizeClasses = {
  xs: 'w-1 h-1',
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
  lg: 'w-2.5 h-2.5',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function StatusBadge<T extends StatusType>({
  type,
  status,
  size = 'sm',
  showDot = false,
  bordered = false,
  pill = true,
  label: customLabel,
  className,
}: StatusBadgeProps<T>) {
  // Get status configuration
  const statusConfig = statusColors[type] as Record<string, any>;
  const config = statusConfig?.[status] || statusColors.generic.inactive;

  const { bg, text, border, dot, label } = config;
  const displayLabel = customLabel || label;

  return (
    <span
      className={cn(
        // Base styles
        'inline-flex items-center gap-1.5 font-medium whitespace-nowrap',
        // Size
        sizeClasses[size],
        // Shape
        pill ? 'rounded-full' : 'rounded',
        // Colors
        bg,
        text,
        // Border
        bordered && `border ${border}`,
        // Animation
        'transition-colors duration-150',
        className
      )}
    >
      {showDot && (
        <span
          className={cn(
            'rounded-full flex-shrink-0',
            dotSizeClasses[size],
            dot
          )}
        />
      )}
      {displayLabel}
    </span>
  );
}

// ============================================================================
// PRESET COMPONENTS
// ============================================================================

/**
 * Invoice Status Badge
 */
export function InvoiceStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'invoice'>, 'type'> & { status: InvoiceStatus }) {
  return <StatusBadge type="invoice" status={status} showDot {...props} />;
}

/**
 * Payment Status Badge
 */
export function PaymentStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'payment'>, 'type'> & { status: PaymentStatus }) {
  return <StatusBadge type="payment" status={status} showDot {...props} />;
}

/**
 * Quotation Status Badge
 */
export function QuotationStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'quotation'>, 'type'> & { status: QuotationStatus }) {
  return <StatusBadge type="quotation" status={status} showDot {...props} />;
}

/**
 * Rental Status Badge
 */
export function RentalStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'rental'>, 'type'> & { status: RentalStatus }) {
  return <StatusBadge type="rental" status={status} showDot {...props} />;
}

/**
 * Maintenance Status Badge
 */
export function MaintenanceStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'maintenance'>, 'type'> & { status: MaintenanceStatus }) {
  return <StatusBadge type="maintenance" status={status} showDot {...props} />;
}

/**
 * Contract Status Badge
 */
export function ContractStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'contract'>, 'type'> & { status: ContractStatus }) {
  return <StatusBadge type="contract" status={status} showDot {...props} />;
}

/**
 * Device Status Badge
 */
export function DeviceStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'device'>, 'type'> & { status: DeviceStatus }) {
  return <StatusBadge type="device" status={status} showDot {...props} />;
}

/**
 * Generic Status Badge
 */
export function GenericStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps<'generic'>, 'type'> & { status: GenericStatus }) {
  return <StatusBadge type="generic" status={status} showDot {...props} />;
}

// ============================================================================
// SIMPLE BADGES (for non-status use cases)
// ============================================================================

interface SimpleBadgeProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  pill?: boolean;
  className?: string;
}

const variantClasses = {
  primary: 'bg-primary-100 text-primary-700',
  secondary: 'bg-secondary-100 text-secondary-700',
  success: 'bg-success-100 text-success-700',
  warning: 'bg-warning-100 text-warning-700',
  danger: 'bg-danger-100 text-danger-700',
  info: 'bg-info-100 text-info-700',
  accent: 'bg-accent-100 text-accent-700',
};

export function Badge({
  children,
  variant = 'secondary',
  size = 'sm',
  pill = true,
  className,
}: SimpleBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium whitespace-nowrap',
        sizeClasses[size],
        pill ? 'rounded-full' : 'rounded',
        variantClasses[variant],
        className
      )}
    >
      {children}
    </span>
  );
}

// ============================================================================
// COUNT BADGE (for notification counts, etc.)
// ============================================================================

interface CountBadgeProps {
  count: number;
  max?: number;
  variant?: 'primary' | 'danger' | 'success' | 'warning';
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

// S722 a11y FIX: success-600 (3.74) + warning-600 (3.19) fail WCAG 2 AA 4.5:1 for normal text.
// Bumped success → success-700 (5.47 PASS) · warning → warning-700 (5.02 PASS).
// primary-600 (7.07) + danger-600 (4.83) already PASS so kept.
const countVariantClasses = {
  primary: 'bg-primary-600 text-white',
  danger: 'bg-danger-600 text-white',
  success: 'bg-success-700 text-white',
  warning: 'bg-warning-700 text-white',
};

const countSizeClasses = {
  xs: 'min-w-4 h-4 text-2xs px-1',
  sm: 'min-w-5 h-5 text-xs px-1.5',
  md: 'min-w-6 h-6 text-xs px-2',
};

export function CountBadge({
  count,
  max = 99,
  variant = 'danger',
  size = 'sm',
  className,
}: CountBadgeProps) {
  const displayCount = count > max ? `${max}+` : count;

  if (count <= 0) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center font-semibold rounded-full',
        countVariantClasses[variant],
        countSizeClasses[size],
        className
      )}
    >
      {displayCount}
    </span>
  );
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default StatusBadge;
